/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useStore } from "../../contexts/StoreContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Modal } from "../../components/ui/Modal";
import FormPreview from "../forms/components/FormPreview";
import { CURRENCY_OPTIONS, currencyOptionsFor } from "../../constants/currencies";
import { walletLabels } from "../../utils/passUtils";
import { notifyError } from "../../utils/bugsnag";
import { track, trackOnce } from "../../utils/analytics";
import { useStartData } from "./StartContext";
import { useFlyerJob } from "./FlyerJob";
import * as startApi from "./api";
import {
  BUSINESS_TYPES,
  CARD_COLORS,
  CARD_TYPES,
  STAMP_COUNT_MAX,
  STAMP_COUNT_MIN,
  getBusinessType,
  stampIconShapes,
  stampIconSvgDataUrl,
  defaultCurrency,
  defaultPaperSize,
} from "./presets";
import { buildPassFormData, cardProblems, stripSignature } from "./passBuilder";
import { buildFormFields, formDescriptionHtml, formDescriptionText } from "./formBuilder";
import { captureCardImages } from "./cardImages";
import { OWNER_CARD_KEYS, ensureFlyerRecord, saveForm, savePass, saveStore } from "./setupChain";
import { safeStorage } from "../../utils/storage";
import {
  dataUrlToBlob,
  makeMonogramLogo,
  normalizeLogo,
  readableTextColor,
  stampIconPng,
} from "./imaging";
import { defaultDraft, draftFromServer, loadDraft, loadLogo, saveDraft, saveLogo } from "./draft";
import { CaptureSource, CardPreviewFront, FullCardDialog } from "./CardPreview";
import {
  Chip,
  ErrorBox,
  Field,
  InfoBox,
  LinkButton,
  PrimaryButton,
  Section,
  Spinner,
  StickyFooter,
  Stepper,
  TextInput,
  Toggle,
  cx,
} from "./ui";

const NAME_MIN = 2;
const NAME_MAX = 50;

function nameError(name) {
  const trimmed = String(name || "").trim();
  if (trimmed.length < NAME_MIN) return "short";
  if (trimmed.length > NAME_MAX) return "long";
  return null;
}

function IconGlyph({ id, className = "h-5 w-5" }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("inline-block", className)}
    >
      {stampIconShapes(id).map(({ tag: Tag, props }, i) => (
        <Tag key={i} {...props} />
      ))}
    </svg>
  );
}

const SAVE_STEPS = ["store", "images", "card", "form", "flyer"];

export default function Customize() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { updateSelectedStore } = useStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const data = useStartData();
  const flyerJob = useFlyerJob();
  const uid = user?.uid;

  // Translations load asynchronously; until they do t() returns the key, which
  // must never end up as a merchant's reward text.
  const presetText = useCallback(
    (type, key) => {
      const fullKey = `start.presets.${type}.${key}`;
      const value = t(fullKey);
      return value === fullKey ? "" : value;
    },
    [t],
  );

  const [draft, setDraft] = useState(() => {
    const saved = loadDraft(uid);
    // A draft belongs to one store: a second store (added later through
    // /stores/new) must not open with the first one's answers.
    const sameStore =
      saved && (!saved.storeId || !data.storeId || String(saved.storeId) === String(data.storeId));
    if (sameStore) return saved;
    if (saved) saveLogo(uid, null);
    const base = defaultDraft({ presetText });
    if (data.store || data.startPass) {
      return draftFromServer({ store: data.store, pass: data.startPass, form: data.initForm, base });
    }
    return base;
  });
  const update = useCallback((patch) => setDraft((d) => ({ ...d, ...(typeof patch === "function" ? patch(d) : patch) })), []);
  const touch = useCallback(
    (field, patch) => setDraft((d) => ({ ...d, ...patch, touched: { ...d.touched, [field]: true } })),
    [],
  );

  useEffect(() => {
    saveDraft(uid, draft);
  }, [uid, draft]);

  useEffect(() => {
    if (data.storeId && String(draft.storeId || "") !== String(data.storeId)) update({ storeId: data.storeId });
  }, [data.storeId, draft.storeId, update]);

  useEffect(() => {
    trackOnce(`customize:${uid}`, "start_customize_viewed");
  }, [uid]);

  // Fill example text the merchant has not typed over, in the current language
  // (also once translations arrive after the first render).
  useEffect(() => {
    setDraft((d) => {
      const touched = d.touched || {};
      const next = { ...d };
      let changed = false;
      const fill = (field, value, apply) => {
        if (!touched[field] && value && apply(next) !== value) {
          changed = true;
          return true;
        }
        return false;
      };
      const reward = presetText(d.businessType, "reward");
      if (fill("reward", reward, (x) => x.reward)) next.reward = reward;
      if (fill("couponOffer", reward, (x) => x.couponOffer)) next.couponOffer = reward;
      const milestone = presetText(d.businessType, "milestone");
      if (fill("midReward", milestone, (x) => x.midReward?.label)) {
        next.midReward = { ...d.midReward, label: milestone };
      }
      return changed ? next : d;
    });
  }, [presetText, draft.businessType]);

  // Country (from the IP) picks the currency and the flyer's paper size.
  useEffect(() => {
    if (draft.country) return;
    let active = true;
    startApi.detectCountry().then((country) => {
      if (!active || !country) return;
      setDraft((d) => ({
        ...d,
        country,
        currency: d.currency || data.store?.currency || defaultCurrency(country),
        paperSize: d.touched?.paperSize ? d.paperSize : defaultPaperSize(country),
      }));
    });
    return () => {
      active = false;
    };
  }, [draft.country, data.store?.currency]);

  const business = getBusinessType(draft.businessType);
  const textColor = readableTextColor(draft.cardColor);
  const storeName = String(draft.storeName || "").trim();
  const nameProblem = nameError(draft.storeName);
  const problems = cardProblems(draft);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [nameBlurred, setNameBlurred] = useState(false);

  // ---- Logo: an upload, or the initials logo drawn from the name and colour.
  const [uploadedLogo, setUploadedLogo] = useState(() => loadLogo(uid));
  const [monogram, setMonogram] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const fileInputRef = useRef(null);
  const [serverLogoUrl, setServerLogoUrl] = useState(null);

  useEffect(() => {
    let active = true;
    const id = setTimeout(async () => {
      try {
        const result = await makeMonogramLogo(storeName || t("start.customize.store.yourStore"), {
          background: draft.cardColor,
          foreground: textColor,
          lang: language,
        });
        if (active) setMonogram(result);
      } catch {
        // the preview simply shows no logo
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [storeName, draft.cardColor, textColor, language, t]);

  // Someone who uploaded a logo on another phone: show the saved one.
  useEffect(() => {
    if (draft.logoKind !== "upload" || uploadedLogo || !data.store?.logo_url) return undefined;
    let active = true;
    let objectUrl = null;
    fetch(data.store.logo_url, { mode: "cors", cache: "no-store" })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!active || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setServerLogoUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [draft.logoKind, uploadedLogo, data.store?.logo_url]);

  const displayLogo =
    draft.logoKind === "upload" ? uploadedLogo?.dataUrl || serverLogoUrl || monogram?.dataUrl : monogram?.dataUrl;

  const handleLogoFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const result = await normalizeLogo(file);
      const logo = { dataUrl: result.dataUrl, color: result.color };
      setUploadedLogo(logo);
      saveLogo(uid, logo);
      setDraft((d) => ({
        ...d,
        logoKind: "upload",
        logoUploaded: false,
        cardColor: result.color && !d.touched?.cardColor ? result.color : d.cardColor,
      }));
      track("start_logo_uploaded");
    } catch (error) {
      setLogoError(t("start.customize.store.logoError"));
      track("start_logo_failed", { reason: String(error?.message || "").slice(0, 60) });
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = () => {
    setUploadedLogo(null);
    saveLogo(uid, null);
    setServerLogoUrl(null);
    update({ logoKind: "monogram", logoUploaded: false, monogramSignature: null });
  };

  // ---- Stamp icon, drawn in the card's text colour.
  const [iconPng, setIconPng] = useState(null);
  const iconSvgUrl = stampIconSvgDataUrl(draft.iconId, textColor);
  useEffect(() => {
    let active = true;
    stampIconPng(iconSvgUrl)
      .then((result) => active && setIconPng(result))
      .catch(() => active && setIconPng(null));
    return () => {
      active = false;
    };
  }, [iconSvgUrl]);
  const stampIconSource = iconPng?.dataUrl || iconSvgUrl;

  // ---- The card as the wallet will show it.
  const wl = walletLabels(t);
  const formData = useMemo(
    () =>
      buildPassFormData(
        { ...draft, storeName: storeName || t("start.customize.store.yourStore") },
        { t, wl, user, store: data.store, logoUrl: displayLogo, stampIconUrl: stampIconSource },
      ),
    // wl is rebuilt from t on every render; its text is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, storeName, t, user, data.store, displayLogo, stampIconSource, Object.values(wl).join("|")],
  );

  // ---- Create the store as soon as it has a name, like /setup does on its
  // first step: the account then has a store even if the person stops here,
  // which is what the setup reminder emails and Intercom key on.
  //
  // There is exactly one store creation per screen, shared by this early
  // create and the save button: the backend accepts any number of stores with
  // the same name, so two overlapping POSTs (a quick tap on Continue, a slow
  // network) would leave the card in one store and the selection on another.
  const storeCreation = useRef(null); // the in-flight or finished create
  const createdStoreId = useRef(null);
  const earlyCreateTimer = useRef(null);
  const selectStore = useRef(updateSelectedStore);
  selectStore.current = updateSelectedStore;
  const [saving, setSaving] = useState(null);

  const createStoreOnce = useCallback(
    (fields) => {
      if (!storeCreation.current) {
        storeCreation.current = saveStore({ storeId: null, fields })
          .then((created) => {
            createdStoreId.current = created.id;
            selectStore.current(created.id);
            return created;
          })
          .catch((error) => {
            storeCreation.current = null;
            throw error;
          });
      }
      return storeCreation.current;
    },
    [],
  );

  useEffect(() => {
    if (data.storeId || createdStoreId.current || storeCreation.current || saving || nameProblem) return undefined;
    earlyCreateTimer.current = setTimeout(async () => {
      if (storeCreation.current || createdStoreId.current) return;
      try {
        await createStoreOnce({
          name: storeName,
          currency: draft.currency || "€",
          timeZone: draft.timeZone,
          backgroundColor: draft.cardColor,
          primaryColor: textColor,
          secondaryColor: textColor,
        });
        await queryClient.invalidateQueries({ queryKey: ["stores"] });
        track("start_store_created");
      } catch {
        // Saving the card creates it later.
      }
    }, 1500);
    return () => clearTimeout(earlyCreateTimer.current);
  }, [data.storeId, saving, nameProblem, storeName, draft.currency, draft.timeZone, draft.cardColor, textColor, queryClient, createStoreOnce]);

  // ---- Keep the field being typed in visible above the keyboard: shrink the
  // card preview while an input has focus. It grows back a moment after the
  // input loses focus, never at once: tapping a button blurs the input on
  // touch-down, and a preview growing between touch-down and touch-up moves the
  // button out from under the finger, so the tap is lost.
  const [typing, setTyping] = useState(false);
  const expandTimer = useRef(null);
  useEffect(() => () => clearTimeout(expandTimer.current), []);
  const onFocusCapture = (e) => {
    if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) && e.target.type !== "file") {
      clearTimeout(expandTimer.current);
      setTyping(true);
    }
  };
  const onBlurCapture = () => {
    clearTimeout(expandTimer.current);
    expandTimer.current = setTimeout(() => setTyping(false), 400);
  };

  // ---- Save everything and move on.
  const [saveError, setSaveError] = useState(null);
  const captureRootRef = useRef(null);
  const stripRef = useRef(null);
  const nameRef = useRef(null);
  const cardSectionRef = useRef(null);

  const save = async () => {
    setSaveAttempted(true);
    if (nameProblem) {
      nameRef.current?.focus();
      nameRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      return;
    }
    if (Object.keys(problems).length) {
      cardSectionRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
      return;
    }
    setSaveError(null);
    clearTimeout(earlyCreateTimer.current);
    const colors = { backgroundColor: draft.cardColor, textColor };
    let step = "store";
    const enter = (next) => {
      step = next;
      setSaving(next);
    };
    try {
      enter("store");
      let logo = null;
      let monogramSignature = draft.monogramSignature;
      if (draft.logoKind === "upload") {
        if (!draft.logoUploaded && uploadedLogo?.dataUrl) logo = dataUrlToBlob(uploadedLogo.dataUrl);
      } else {
        const signature = `${storeName}|${draft.cardColor}|${textColor}`;
        if (signature !== draft.monogramSignature || !data.store?.logo_url) {
          logo = (await makeMonogramLogo(storeName, { background: draft.cardColor, foreground: textColor, lang: language })).blob;
          monogramSignature = signature;
        }
      }
      const storeFields = {
        name: storeName,
        currency: draft.currency || data.store?.currency || "€",
        timeZone: draft.timeZone,
        backgroundColor: draft.cardColor,
        primaryColor: textColor,
        secondaryColor: textColor,
        logo,
      };
      // Reuse the store the early create made (or is still making).
      let existingStoreId = data.storeId || createdStoreId.current;
      if (!existingStoreId && storeCreation.current) {
        existingStoreId = (await storeCreation.current.catch(() => null))?.id;
      }
      const savedStore = existingStoreId
        ? await saveStore({ storeId: existingStoreId, fields: storeFields })
        : await createStoreOnce(storeFields);
      setDraft((d) => ({ ...d, logoUploaded: d.logoKind === "upload" ? true : d.logoUploaded, monogramSignature }));

      enter("images");
      const signature = stripSignature(formData);
      const typeChanged = !!data.startPass && (data.startPass.pass?.type || "stamp") !== formData.type;
      const images = await captureCardImages({
        captureRoot: captureRootRef.current,
        stripElement: stripRef.current,
        formData,
        stampIconBlob: iconPng?.blob,
        includeStrips: !data.startPass || typeChanged || signature !== draft.savedStripSignature,
      });

      enter("card");
      const { pass, recreated } = await savePass({
        storeId: savedStore.id,
        store: savedStore,
        formData,
        images,
        existingPass: data.startPass,
      });
      // A new card means the owner's test card on the old one is gone too.
      if (recreated) OWNER_CARD_KEYS.forEach((key) => safeStorage.remove(key));

      enter("form");
      const form = await saveForm({
        storeId: savedStore.id,
        passId: pass.id,
        draft: { ...draft, storeName },
        t,
        lang: language,
        colors,
      });

      enter("flyer");
      const flyer = await ensureFlyerRecord({
        storeId: savedStore.id,
        formId: form.id,
        paperSize: draft.paperSize || "a4",
        colors,
        t,
      });
      const lookSignature = `${storeName}|${draft.cardColor}|${draft.logoKind}|${uploadedLogo?.dataUrl?.length || 0}`;
      // The PDF is drawn in the background; Try it does not need it, and the
      // Go live and Done steps make sure it exists.
      flyerJob
        .ensurePdf({
          force: !!flyer.pdf_url && lookSignature !== draft.savedLookSignature,
          flyer,
          form,
          store: savedStore,
          logoUrl: displayLogo,
        })
        .catch(() => {});

      setDraft((d) => ({ ...d, savedStripSignature: signature, savedLookSignature: lookSignature }));
      await startApi.checkSetup(savedStore.id).catch(() => null);
      await data.refresh();
      track("start_card_saved", {
        card_type: draft.cardType,
        business_type: draft.businessType,
        logo: draft.logoKind,
        back_rows: Object.values(draft.back || {}).filter((row) => row.enabled).length,
        phone_field: !!draft.form?.phone,
        birthday_field: !!draft.form?.birthday,
      });
      navigate("/start/try");
    } catch (error) {
      setSaveError(error);
      // A retry must see what this attempt already created.
      data.refresh().catch(() => {});
      track("start_card_save_failed", { step, status: error?.response?.status || 0 });
      notifyError(error, (event) => {
        event.context = "start.customize.save";
      });
    } finally {
      setSaving(null);
    }
  };

  const [fullCardOpen, setFullCardOpen] = useState(false);
  const [formPreviewOpen, setFormPreviewOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);

  const businessLabel = (id) => t(`start.presets.${id}.label`);
  const typeLabel = (type) => t(`passes.form.passType.${type}`);
  const iconChoices = Array.from(new Set([business.icon, "star", "heart", "gift", "cup"])).slice(0, 5);
  const currencyLabel = (CURRENCY_OPTIONS.find((o) => o.value === (draft.currency || "€")) || { label: draft.currency }).label;
  const showNameError = (saveAttempted || nameBlurred) && nameProblem;
  const formFields = buildFormFields(t, draft.form);
  const bandLabel = t(`start.card.band.${draft.cardType}`, {
    discount: Number(draft.discount) || 0,
    offer: draft.couponOffer || "",
  });

  const errorMessage = (() => {
    if (!saveError) return null;
    const errors = saveError?.response?.data?.errors;
    if (Array.isArray(errors) && errors.length) return `${t("start.customize.save.failed")} (${errors.join(", ")})`;
    if (!saveError?.response) return t("start.customize.save.offline");
    return t("start.customize.save.failed");
  })();

  return (
    <div onFocusCapture={onFocusCapture} onBlurCapture={onBlurCapture}>
      <h1 className="text-2xl font-bold text-gray-900">{t("start.customize.title")}</h1>
      <p className="mt-1 text-base text-gray-600">{t("start.customize.subtitle")}</p>

      {/* Card preview: stays on screen while editing below it. */}
      <div className="sticky top-[104px] z-10 -mx-4 mt-4 bg-[#F9FAFB]/95 px-4 pb-3 pt-2 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="mx-auto max-w-sm">
          <CardPreviewFront formData={formData} stampIconSource={stampIconSource} bandLabel={bandLabel} compact={typing} />
          {!typing && (
            <div className="mt-1 flex justify-center">
              <LinkButton onClick={() => setFullCardOpen(true)}>{t("start.customize.preview.full")}</LinkButton>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {/* ---- Store ---- */}
        <Section title={t("start.customize.store.title")}>
          <Field
            label={t("start.customize.store.nameLabel")}
            htmlFor="start-store-name"
            error={
              showNameError
                ? t(nameProblem === "short" ? "start.customize.store.nameShort" : "start.customize.store.nameLong")
                : null
            }
          >
            <TextInput
              id="start-store-name"
              ref={nameRef}
              value={draft.storeName}
              maxLength={NAME_MAX + 10}
              autoComplete="organization"
              autoCapitalize="words"
              enterKeyHint="done"
              placeholder={t("start.customize.store.namePlaceholder", { example: presetText(draft.businessType, "example") })}
              invalid={!!showNameError}
              onChange={(e) => update({ storeName: e.target.value })}
              onBlur={() => setNameBlurred(true)}
            />
          </Field>

          <div>
            <p className="mb-2 text-sm font-semibold text-gray-800" id="start-business-type">
              {t("start.customize.store.typeLabel")}
            </p>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-labelledby="start-business-type">
              {BUSINESS_TYPES.map((type) => {
                const selected = draft.businessTypeChosen && draft.businessType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      setDraft((d) => {
                        const touched = d.touched || {};
                        const next = { ...d, businessType: type.id, businessTypeChosen: true };
                        if (!touched.cardColor && !(d.logoKind === "upload" && uploadedLogo?.color)) next.cardColor = type.color;
                        if (!touched.iconId) next.iconId = type.icon;
                        const reward = presetText(type.id, "reward");
                        if (!touched.reward && reward) next.reward = reward;
                        if (!touched.couponOffer && reward) next.couponOffer = reward;
                        const milestone = presetText(type.id, "milestone");
                        if (!touched.midReward && milestone) next.midReward = { ...d.midReward, label: milestone };
                        return next;
                      });
                      track("start_business_type_selected", { business_type: type.id });
                    }}
                    className={cx(
                      "flex min-h-[72px] flex-col items-center justify-center gap-1 rounded-xl border px-1 py-2 text-center text-xs font-semibold transition",
                      selected ? "border-[#4522ED] bg-[#EEEBFE] text-[#2F12B8]" : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50",
                    )}
                  >
                    <IconGlyph id={type.icon} className="h-6 w-6" />
                    <span className="leading-tight">{businessLabel(type.id)}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-sm text-gray-500">{t("start.customize.store.typeHint")}</p>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-gray-800">{t("start.customize.store.logoLabel")}</p>
            <div className="flex items-center gap-3">
              {displayLogo ? (
                <img src={displayLogo} alt="" className="h-14 w-14 rounded-full border border-gray-200 object-contain" />
              ) : (
                <div className="h-14 w-14 rounded-full border border-dashed border-gray-300" />
              )}
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={handleLogoFile}
                />
                <button
                  type="button"
                  disabled={logoBusy}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-60"
                >
                  {logoBusy && <Spinner className="h-4 w-4 text-gray-700" />}
                  {draft.logoKind === "upload" ? t("start.customize.store.logoChange") : t("start.customize.store.logoUpload")}
                </button>
                {draft.logoKind === "upload" && (
                  <LinkButton onClick={removeLogo}>{t("start.customize.store.logoRemove")}</LinkButton>
                )}
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              {draft.logoKind === "upload" ? t("start.customize.store.logoHintUploaded") : t("start.customize.store.logoHint")}
            </p>
            {logoError && (
              <p className="mt-2 text-sm font-medium text-red-700" role="alert">
                {logoError}
              </p>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-gray-800" id="start-card-color">
              {t("start.customize.store.colorLabel")}
            </p>
            <div className="flex flex-wrap items-center gap-2.5" role="radiogroup" aria-labelledby="start-card-color">
              {uploadedLogo?.color && draft.logoKind === "upload" && !CARD_COLORS.includes(uploadedLogo.color) && (
                <button
                  type="button"
                  role="radio"
                  aria-checked={draft.cardColor === uploadedLogo.color}
                  aria-label={t("start.customize.store.logoColor")}
                  onClick={() => touch("cardColor", { cardColor: uploadedLogo.color })}
                  className={cx(
                    "h-11 w-11 rounded-full border-2 border-white",
                    draft.cardColor === uploadedLogo.color && "ring-2 ring-[#4522ED]",
                  )}
                  style={{ backgroundColor: uploadedLogo.color }}
                />
              )}
              {CARD_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  role="radio"
                  aria-checked={draft.cardColor === color}
                  aria-label={t("start.customize.store.colorOption", { color })}
                  onClick={() => touch("cardColor", { cardColor: color })}
                  className={cx(
                    "h-11 w-11 rounded-full border-2 border-white shadow-sm",
                    draft.cardColor === color && "ring-2 ring-[#4522ED]",
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
              <label
                className={cx(
                  "relative flex h-11 w-11 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-white shadow-sm",
                  !CARD_COLORS.includes(draft.cardColor) && draft.cardColor !== uploadedLogo?.color && "ring-2 ring-[#4522ED]",
                )}
                style={{
                  background: !CARD_COLORS.includes(draft.cardColor) && draft.cardColor !== uploadedLogo?.color
                    ? draft.cardColor
                    : "conic-gradient(#EF4444,#F59E0B,#EAB308,#22C55E,#06B6D4,#3B82F6,#8B5CF6,#EC4899,#EF4444)",
                }}
              >
                <span className="sr-only">{t("start.customize.store.colorCustom")}</span>
                <input
                  type="color"
                  value={draft.cardColor}
                  onChange={(e) => touch("cardColor", { cardColor: e.target.value.toUpperCase() })}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
              </label>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {t("start.customize.store.region", { currency: currencyLabel, timeZone: draft.timeZone || "UTC" })}{" "}
            <button type="button" className="min-h-11 font-semibold text-[#4522ED]" onClick={() => setRegionOpen((v) => !v)} aria-expanded={regionOpen}>
              {t("start.customize.store.regionChange")}
            </button>
            {regionOpen && (
              <div className="mt-2">
                <Field label={t("store.currency")} htmlFor="start-currency">
                  <select
                    id="start-currency"
                    value={draft.currency || "€"}
                    onChange={(e) => update({ currency: e.target.value })}
                    className="block min-h-12 w-full rounded-xl border border-gray-300 bg-white px-3 text-base"
                  >
                    {currencyOptionsFor(draft.currency).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <p className="mt-2 text-sm text-gray-500">{t("start.customize.store.timeZoneLater")}</p>
              </div>
            )}
          </div>
        </Section>

        {/* ---- Card ---- */}
        <div ref={cardSectionRef}>
          <Section title={t("start.card.title")} description={t("start.card.subtitle")}>
            <div>
              <p className="mb-2 text-sm font-semibold text-gray-800" id="start-card-type">
                {t("start.card.typeLabel")}
              </p>
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" role="radiogroup" aria-labelledby="start-card-type">
                {CARD_TYPES.map((type) => (
                  <Chip
                    key={type}
                    role="radio"
                    aria-checked={draft.cardType === type}
                    aria-pressed={undefined}
                    selected={draft.cardType === type}
                    onClick={() => {
                      update({ cardType: type });
                      track("start_card_type_selected", { card_type: type });
                    }}
                  >
                    {typeLabel(type)}
                  </Chip>
                ))}
              </div>
              {draft.businessTypeChosen && business.recommendedType !== draft.cardType && (
                <p className="mt-2 text-sm text-gray-600">
                  {t("start.card.recommended", { business: businessLabel(business.id), type: typeLabel(business.recommendedType) })}{" "}
                  <button type="button" className="min-h-11 font-semibold text-[#4522ED]" onClick={() => update({ cardType: business.recommendedType })}>
                    {t("start.card.useRecommended")}
                  </button>
                </p>
              )}
            </div>

            {draft.cardType === "stamp" && (
              <>
                <Field
                  label={t("start.card.rewardLabel")}
                  htmlFor="start-reward"
                  error={saveAttempted && problems.reward ? t("start.card.rewardRequired") : null}
                >
                  <TextInput
                    id="start-reward"
                    value={draft.reward}
                    maxLength={60}
                    placeholder={t("start.card.rewardPlaceholder")}
                    invalid={saveAttempted && !!problems.reward}
                    onChange={(e) => touch("reward", { reward: e.target.value })}
                  />
                </Field>
                <Stepper
                  label={t("start.card.stampsLabel")}
                  value={Number(draft.stampCount)}
                  min={STAMP_COUNT_MIN}
                  max={STAMP_COUNT_MAX}
                  decrementLabel={t("start.card.stampsDecrease")}
                  incrementLabel={t("start.card.stampsIncrease")}
                  onChange={(value) =>
                    update((d) => ({
                      stampCount: value,
                      midReward: { ...d.midReward, position: Math.min(d.midReward?.position || 1, value - 1) },
                    }))
                  }
                />
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-800" id="start-stamp-icon">
                    {t("start.card.iconLabel")}
                  </p>
                  <div className="flex gap-2" role="radiogroup" aria-labelledby="start-stamp-icon">
                    {iconChoices.map((id) => (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={draft.iconId === id}
                        aria-label={t(`start.card.icons.${id}`)}
                        onClick={() => touch("iconId", { iconId: id })}
                        className={cx(
                          "flex h-12 w-12 items-center justify-center rounded-xl border",
                          draft.iconId === id ? "border-[#4522ED] bg-[#EEEBFE] text-[#2F12B8]" : "border-gray-200 bg-white text-gray-700",
                        )}
                      >
                        <IconGlyph id={id} className="h-6 w-6" />
                      </button>
                    ))}
                  </div>
                </div>
                {draft.midReward?.enabled ? (
                  <div className="rounded-xl border border-gray-200 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800">{t("start.card.midTitle")}</p>
                      <LinkButton className="text-red-700" onClick={() => update((d) => ({ midReward: { ...d.midReward, enabled: false } }))}>
                        {t("start.card.midRemove")}
                      </LinkButton>
                    </div>
                    <div className="mt-2 space-y-3">
                      <Stepper
                        label={t("start.card.midPosition")}
                        value={Number(draft.midReward.position) || 1}
                        min={1}
                        max={Math.max(1, Number(draft.stampCount) - 1)}
                        decrementLabel={t("start.card.stampsDecrease")}
                        incrementLabel={t("start.card.stampsIncrease")}
                        onChange={(value) => update((d) => ({ midReward: { ...d.midReward, position: value } }))}
                      />
                      <Field
                        label={t("start.card.midLabel")}
                        htmlFor="start-mid-reward"
                        error={saveAttempted && problems.midReward ? t("start.card.rewardRequired") : null}
                      >
                        <TextInput
                          id="start-mid-reward"
                          value={draft.midReward.label}
                          maxLength={60}
                          invalid={saveAttempted && !!problems.midReward}
                          onChange={(e) => touch("midReward", { midReward: { ...draft.midReward, label: e.target.value } })}
                        />
                      </Field>
                    </div>
                  </div>
                ) : (
                  Number(draft.stampCount) > 2 && (
                    <LinkButton
                      className="px-0"
                      onClick={() =>
                        update((d) => ({
                          midReward: {
                            ...d.midReward,
                            enabled: true,
                            position: Math.max(1, Math.floor(Number(d.stampCount) / 2)),
                          },
                        }))
                      }
                    >
                      + {t("start.card.midAdd")}
                    </LinkButton>
                  )
                )}
              </>
            )}

            {draft.cardType === "discount" && (
              <Field
                label={t("start.card.discountLabel")}
                htmlFor="start-discount"
                error={saveAttempted && problems.discount ? t("start.card.discountInvalid") : null}
              >
                <div className="relative">
                  <TextInput
                    id="start-discount"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={100}
                    value={draft.discount}
                    invalid={saveAttempted && !!problems.discount}
                    onChange={(e) => update({ discount: e.target.value })}
                    className="pr-10"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">%</span>
                </div>
              </Field>
            )}

            {draft.cardType === "coupon" && (
              <Field
                label={t("start.card.couponOfferLabel")}
                htmlFor="start-coupon"
                error={saveAttempted && problems.couponOffer ? t("start.card.rewardRequired") : null}
              >
                <TextInput
                  id="start-coupon"
                  value={draft.couponOffer}
                  maxLength={60}
                  placeholder={t("start.card.rewardPlaceholder")}
                  invalid={saveAttempted && !!problems.couponOffer}
                  onChange={(e) => touch("couponOffer", { couponOffer: e.target.value })}
                />
              </Field>
            )}

            {draft.cardType !== "stamp" && (
              <InfoBox tone="gray">{t(`start.card.typeNotes.${draft.cardType}`)}</InfoBox>
            )}
          </Section>
        </div>

        {/* ---- Back of the card ---- */}
        <Section title={t("start.back.title")} description={t("start.back.subtitle")}>
          <Toggle
            label={t("start.back.instagram.label")}
            checked={draft.back.instagram.enabled}
            onChange={(enabled) => update((d) => ({ back: { ...d.back, instagram: { ...d.back.instagram, enabled } } }))}
          />
          {draft.back.instagram.enabled && (
            <TextInput
              aria-label={t("start.back.instagram.label")}
              value={draft.back.instagram.value}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={t("start.back.instagram.placeholder")}
              onChange={(e) => update((d) => ({ back: { ...d.back, instagram: { ...d.back.instagram, value: e.target.value } } }))}
            />
          )}
          <Toggle
            label={t("start.back.booking.label")}
            checked={draft.back.booking.enabled}
            onChange={(enabled) => update((d) => ({ back: { ...d.back, booking: { ...d.back.booking, enabled } } }))}
          />
          {draft.back.booking.enabled && (
            <div className="space-y-2">
              <TextInput
                aria-label={t("start.back.booking.urlLabel")}
                type="url"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={draft.back.booking.value}
                placeholder={t("start.back.booking.placeholder")}
                onChange={(e) => update((d) => ({ back: { ...d.back, booking: { ...d.back.booking, value: e.target.value } } }))}
              />
              <TextInput
                aria-label={t("start.back.booking.nameLabel")}
                value={draft.back.booking.label}
                maxLength={30}
                placeholder={t("start.back.booking.defaultLabel")}
                onChange={(e) => update((d) => ({ back: { ...d.back, booking: { ...d.back.booking, label: e.target.value } } }))}
              />
            </div>
          )}
          <Toggle
            label={t("start.back.hours.label")}
            checked={draft.back.hours.enabled}
            onChange={(enabled) => update((d) => ({ back: { ...d.back, hours: { ...d.back.hours, enabled } } }))}
          />
          {draft.back.hours.enabled && (
            <TextInput
              aria-label={t("start.back.hours.label")}
              value={draft.back.hours.value}
              maxLength={80}
              placeholder={t("start.back.hours.placeholder")}
              onChange={(e) => update((d) => ({ back: { ...d.back, hours: { ...d.back.hours, value: e.target.value } } }))}
            />
          )}
          <Toggle
            label={t("start.back.info.label")}
            description={t("start.back.info.description")}
            checked={draft.back.info.enabled}
            onChange={(enabled) => update((d) => ({ back: { ...d.back, info: { ...d.back.info, enabled } } }))}
          />
          {draft.back.info.enabled && (
            <div className="space-y-2">
              <TextInput
                aria-label={t("start.back.info.titleLabel")}
                value={draft.back.info.label}
                maxLength={30}
                placeholder={t("start.back.info.titlePlaceholder")}
                onChange={(e) => update((d) => ({ back: { ...d.back, info: { ...d.back.info, label: e.target.value } } }))}
              />
              <TextInput
                aria-label={t("start.back.info.valueLabel")}
                value={draft.back.info.value}
                maxLength={120}
                placeholder={t("start.back.info.valuePlaceholder")}
                onChange={(e) => update((d) => ({ back: { ...d.back, info: { ...d.back.info, value: e.target.value } } }))}
              />
            </div>
          )}
        </Section>

        {/* ---- Sign-up form ---- */}
        <Section title={t("start.form.title")} description={t("start.form.subtitle")}>
          <Toggle label={t("forms.new.initial.name")} description={t("start.form.fields.name")} locked={t("start.form.required")} />
          <Toggle label={t("forms.new.initial.email")} description={t("start.form.fields.email")} locked={t("start.form.required")} />
          <Toggle
            label={t("forms.new.initial.birthday")}
            description={t("start.form.fields.birthday")}
            checked={draft.form.birthday}
            onChange={(birthday) => update((d) => ({ form: { ...d.form, birthday } }))}
          />
          <Toggle
            label={t("forms.new.initial.phone")}
            description={t("start.form.fields.phone")}
            checked={draft.form.phone}
            onChange={(phone) => update((d) => ({ form: { ...d.form, phone } }))}
          />
          <Toggle
            label={t("start.form.marketingLabel")}
            description={t("start.form.fields.marketing")}
            checked={draft.form.marketing}
            onChange={(marketing) => update((d) => ({ form: { ...d.form, marketing } }))}
          />
          <LinkButton className="px-0" onClick={() => setFormPreviewOpen(true)}>
            {t("start.form.preview")}
          </LinkButton>
        </Section>

        <InfoBox tone="gray">{t("start.customize.later")}</InfoBox>
      </div>

      {errorMessage && (
        <div className="mt-4">
          <ErrorBox onRetry={save} retryLabel={t("start.shell.retry")}>
            {errorMessage}
          </ErrorBox>
        </div>
      )}

      <StickyFooter>
        {saveAttempted && (nameProblem || Object.keys(problems).length > 0) && (
          <p className="mb-2 text-center text-sm font-medium text-red-700" role="alert">
            {t("start.customize.save.fixErrors")}
          </p>
        )}
        <PrimaryButton onClick={save} loading={!!saving}>
          {t("start.customize.save.cta")}
        </PrimaryButton>
      </StickyFooter>

      {saving && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-white/85 px-6 backdrop-blur-sm" role="status" aria-live="polite">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-lg">
            <p className="text-lg font-semibold text-gray-900">{t("start.customize.save.title")}</p>
            <ol className="mt-3 space-y-2">
              {SAVE_STEPS.map((step) => {
                const index = SAVE_STEPS.indexOf(step);
                const current = SAVE_STEPS.indexOf(saving);
                return (
                  <li key={step} className="flex items-center gap-2 text-sm">
                    {index < current ? (
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white" aria-hidden="true">
                        <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="currentColor"><path d="M7.7 13.3 4.4 10l-1.1 1.1 4.4 4.4 9-9-1.1-1.1z" /></svg>
                      </span>
                    ) : index === current ? (
                      <Spinner className="h-5 w-5 text-[#4522ED]" />
                    ) : (
                      <span className="h-5 w-5 rounded-full border-2 border-gray-200" aria-hidden="true" />
                    )}
                    <span className={index === current ? "font-semibold text-gray-900" : "text-gray-600"}>
                      {t(`start.customize.save.steps.${step}`)}
                    </span>
                  </li>
                );
              })}
            </ol>
            <p className="mt-3 text-sm text-gray-500">{t("start.customize.save.keepOpen")}</p>
          </div>
        </div>
      )}

      <CaptureSource rootRef={captureRootRef} stripRef={stripRef} formData={formData} stampIconSource={stampIconSource} />

      <FullCardDialog open={fullCardOpen} onClose={() => setFullCardOpen(false)} formData={formData} stampIconSource={stampIconSource} />

      <Modal open={formPreviewOpen} onClose={() => setFormPreviewOpen(false)} title={t("start.form.previewTitle")}>
        <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200">
          <FormPreview
            selectedStore={{ logo_url: displayLogo, name: storeName || t("start.customize.store.yourStore") }}
            formData={{ backgroundColor: draft.cardColor, textColor }}
            description={formDescriptionHtml(formDescriptionText({ ...draft, storeName }, t))}
            fields={formFields}
            submitText={t("forms.new.initial.submitText")}
            handleFieldChange={() => {}}
          />
        </div>
        <button type="button" onClick={() => setFormPreviewOpen(false)} className="min-h-11 w-full rounded-xl border border-gray-300 font-semibold text-gray-900">
          {t("start.customize.preview.close")}
        </button>
      </Modal>
    </div>
  );
}
